import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../../utils/translation";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { listStatisticsData, setStatisticsPeriod } from "../statisticsSlice";
import {
  selectStatisticsConnectionSummary,
  selectStatisticsConnections,
  selectStatisticsError,
  selectStatisticsLoading,
  selectStatisticsMeetings,
  selectStatisticsMessage,
  selectStatisticsPeriod,
  selectStatisticsTranscripts,
  selectStatisticsAssemblyAI,
} from "../statisticsSelectors";
import { STATISTICS_PERIODS } from "../statistics.types";

const DAILY_BUCKET_COUNT = 24;
const WEEKLY_BUCKET_COUNT = 7;
const MONTHLY_BUCKET_COUNT = 30;
const YEARLY_BUCKET_COUNT = 12;

const asMillis = (value) => {
  if (!value) return null;
  if (typeof value === "number") return value;
  return null;
};

const buildBuckets = (period, language) => {
  const now = new Date();

  if (period === STATISTICS_PERIODS.DAILY) {
    const buckets = [];
    const current = new Date(now);
    current.setMinutes(0, 0, 0);

    for (let i = DAILY_BUCKET_COUNT - 1; i >= 0; i -= 1) {
      const start = new Date(current);
      start.setHours(current.getHours() - i);
      const end = new Date(start);
      end.setHours(start.getHours() + 1);
      buckets.push({
        label: start.toLocaleTimeString(language === "tr" ? "tr-TR" : "en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        start: start.getTime(),
        end: end.getTime(),
      });
    }

    return buckets;
  }

  if (period === STATISTICS_PERIODS.WEEKLY || period === STATISTICS_PERIODS.MONTHLY) {
    const bucketCount = period === STATISTICS_PERIODS.WEEKLY ? WEEKLY_BUCKET_COUNT : MONTHLY_BUCKET_COUNT;
    const buckets = [];
    const current = new Date(now);
    current.setHours(0, 0, 0, 0);

    for (let i = bucketCount - 1; i >= 0; i -= 1) {
      const start = new Date(current);
      start.setDate(current.getDate() - i);
      const end = new Date(start);
      end.setDate(start.getDate() + 1);
      buckets.push({
        label: start.toLocaleDateString(language === "tr" ? "tr-TR" : "en-US", {
          day: "2-digit",
          month: "2-digit",
        }),
        start: start.getTime(),
        end: end.getTime(),
      });
    }

    return buckets;
  }

  const buckets = [];
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);

  for (let i = YEARLY_BUCKET_COUNT - 1; i >= 0; i -= 1) {
    const start = new Date(firstOfMonth.getFullYear(), firstOfMonth.getMonth() - i, 1, 0, 0, 0, 0);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 1, 0, 0, 0, 0);
    buckets.push({
      label: start.toLocaleDateString(language === "tr" ? "tr-TR" : "en-US", {
        month: "short",
        year: "2-digit",
      }),
      start: start.getTime(),
      end: end.getTime(),
    });
  }

  return buckets;
};

const countByBuckets = (items, buckets, predicate = () => true) => {
  return buckets.map((bucket) => {
    return items.reduce((acc, item) => {
      const ts = asMillis(item?.createdAt);
      if (!ts) return acc;
      if (ts < bucket.start || ts >= bucket.end) return acc;
      if (!predicate(item)) return acc;
      return acc + 1;
    }, 0);
  });
};

const inRange = (ts, start, end) => Number.isFinite(ts) && ts >= start && ts < end;

const StatisticsDashboardPage = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const meetings = useSelector(selectStatisticsMeetings);
  const transcripts = useSelector(selectStatisticsTranscripts);
  const assemblyAI = useSelector(selectStatisticsAssemblyAI);
  const connections = useSelector(selectStatisticsConnections);
  const connectionSummary = useSelector(selectStatisticsConnectionSummary);
  const isLoading = useSelector(selectStatisticsLoading);
  const isError = useSelector(selectStatisticsError);
  const message = useSelector(selectStatisticsMessage);
  const selectedPeriod = useSelector(selectStatisticsPeriod);

  useEffect(() => {
    dispatch(listStatisticsData());
  }, [dispatch]);

  const periodOptions = useMemo(
    () => [
      { key: STATISTICS_PERIODS.DAILY, label: t("statistics.period.daily") },
      { key: STATISTICS_PERIODS.WEEKLY, label: t("statistics.period.weekly") },
      { key: STATISTICS_PERIODS.MONTHLY, label: t("statistics.period.monthly") },
      { key: STATISTICS_PERIODS.YEARLY, label: t("statistics.period.yearly") },
    ],
    [t]
  );

  const buckets = useMemo(() => buildBuckets(selectedPeriod, i18n.language), [selectedPeriod, i18n.language]);

  const rangeStart = buckets[0]?.start || 0;
  const rangeEnd = buckets[buckets.length - 1]?.end || Date.now();

  const filteredMeetings = useMemo(
    () => meetings.filter((item) => inRange(asMillis(item?.createdAt), rangeStart, rangeEnd)),
    [meetings, rangeStart, rangeEnd]
  );

  const filteredTranscripts = useMemo(
    () => transcripts.filter((item) => inRange(asMillis(item?.createdAt), rangeStart, rangeEnd)),
    [transcripts, rangeStart, rangeEnd]
  );

  const filteredAssembly = useMemo(
    () => assemblyAI.filter((item) => inRange(asMillis(item?.createdAt), rangeStart, rangeEnd)),
    [assemblyAI, rangeStart, rangeEnd]
  );

  const hasConnectionTimeline = useMemo(
    () => connections.some((item) => Number.isFinite(asMillis(item?.createdAt))),
    [connections]
  );

  const filteredConnections = useMemo(() => {
    if (!hasConnectionTimeline) return [];
    return connections.filter((item) => inRange(asMillis(item?.createdAt), rangeStart, rangeEnd));
  }, [connections, rangeStart, rangeEnd, hasConnectionTimeline]);

  const meetingsSeries = useMemo(() => countByBuckets(meetings, buckets), [meetings, buckets]);
  const transcriptsSeries = useMemo(() => countByBuckets(transcripts, buckets), [transcripts, buckets]);
  const assemblySeries = useMemo(() => countByBuckets(assemblyAI, buckets), [assemblyAI, buckets]);
  const agendaSeries = useMemo(
    () => countByBuckets(assemblyAI, buckets, (item) => item?.hasAgenda),
    [assemblyAI, buckets]
  );
  const decisionsSeries = useMemo(
    () => countByBuckets(assemblyAI, buckets, (item) => item?.hasDecisions),
    [assemblyAI, buckets]
  );

  const connectionsSeries = useMemo(() => {
    if (!hasConnectionTimeline) return [];
    return countByBuckets(connections, buckets);
  }, [connections, buckets, hasConnectionTimeline]);

  const totalMeetings = meetings.length;
  const totalTranscripts = transcripts.length;
  const totalAssemblyAI = assemblyAI.length;
  const totalAgendas = assemblyAI.filter((item) => item?.hasAgenda).length;
  const totalDecisions = assemblyAI.filter((item) => item?.hasDecisions).length;
  const totalSpeakers = assemblyAI.filter((item) => item?.hasSpeakers).length;
  const totalSpeechAnalysis = assemblyAI.filter((item) => item?.hasSpeechAnalysis).length;
  const totalCompleted = assemblyAI.filter((item) => item?.completed).length;
  const totalAgendaMatch = assemblyAI.filter((item) => item?.hasAgendaMatchSummary).length;
  const summaryConnections = Number(connectionSummary?.totalConnections || 0);
  const totalConnections = connections.length > 0 ? connections.length : summaryConnections;

  const selectedMeetings = filteredMeetings.length;
  const selectedTranscripts = filteredTranscripts.length;
  const selectedAssemblyAI = filteredAssembly.length;
  const selectedAgendas = filteredAssembly.filter((item) => item?.hasAgenda).length;
  const selectedDecisions = filteredAssembly.filter((item) => item?.hasDecisions).length;
  const selectedSpeakers = filteredAssembly.filter((item) => item?.hasSpeakers).length;
  const selectedSpeechAnalysis = filteredAssembly.filter((item) => item?.hasSpeechAnalysis).length;
  const selectedCompleted = filteredAssembly.filter((item) => item?.completed).length;
  const selectedAgendaMatch = filteredAssembly.filter((item) => item?.hasAgendaMatchSummary).length;
  const selectedConnections = hasConnectionTimeline
    ? filteredConnections.length
    : (connections.length > 0 ? connections.length : summaryConnections);

  const latestTranscripts = useMemo(() => {
    const source = filteredTranscripts.length > 0 ? filteredTranscripts : transcripts;
    return [...source]
      .sort((a, b) => (asMillis(b?.createdAt) || 0) - (asMillis(a?.createdAt) || 0))
      .slice(0, 8);
  }, [filteredTranscripts, transcripts]);

  const lineDatasets = [
    {
      label: t("statistics.metrics.meetings"),
      data: meetingsSeries,
      borderColor: "#1e88e5",
      backgroundColor: "rgba(30, 136, 229, 0.15)",
      tension: 0.3,
    },
    hasConnectionTimeline
      ? {
          label: t("statistics.metrics.connections"),
          data: connectionsSeries,
          borderColor: "#00acc1",
          backgroundColor: "rgba(0, 172, 193, 0.15)",
          tension: 0.3,
        }
      : null,
    {
      label: t("statistics.metrics.transcripts"),
      data: transcriptsSeries,
      borderColor: "#43a047",
      backgroundColor: "rgba(67, 160, 71, 0.12)",
      tension: 0.3,
    },
    {
      label: t("statistics.metrics.assembly_ai"),
      data: assemblySeries,
      borderColor: "#7e57c2",
      backgroundColor: "rgba(126, 87, 194, 0.15)",
      tension: 0.3,
    },
  ].filter(Boolean);

  const lineData = {
    labels: buckets.map((bucket) => bucket.label),
    datasets: lineDatasets,
  };

  const barData = {
    labels: [
      t("statistics.metrics.transcripts"),
      t("statistics.metrics.assembly_ai"),
      t("statistics.metrics.agenda"),
      t("statistics.metrics.decisions"),
    ],
    datasets: [
      {
        label: t("statistics.selected_period"),
        data: [selectedTranscripts, selectedAssemblyAI, selectedAgendas, selectedDecisions],
        backgroundColor: ["#1e88e5", "#8e24aa", "#3949ab", "#00897b"],
      },
    ],
  };

  const doughnutData = {
    labels: [
      t("statistics.metrics.agenda"),
      t("statistics.metrics.decisions"),
      t("statistics.metrics.speakers"),
      t("statistics.metrics.speech_analysis"),
    ],
    datasets: [
      {
        data: [selectedAgendas, selectedDecisions, selectedSpeakers, selectedSpeechAnalysis],
        backgroundColor: ["#42a5f5", "#7e57c2", "#26a69a", "#ef6c00"],
        borderWidth: 1,
      },
    ],
  };

  const locale = i18n.language === "tr" ? "tr-TR" : "en-US";
  const formatNumber = (value) => Number(value || 0).toLocaleString(locale);
  const formatDate = (value) => {
    if (!Number.isFinite(value)) return "-";
    return new Date(value).toLocaleString(locale);
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <h4 className="card-title mb-0">{t("statistics.title")}</h4>
              <div className="btn-group">
                {periodOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`btn ${selectedPeriod === option.key ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => dispatch(setStatisticsPeriod(option.key))}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => dispatch(listStatisticsData({ force: true }))}
                disabled={isLoading}
              >
                {t("statistics.refresh")}
              </button>
            </div>

            {isError ? (
              <div className="alert alert-danger mb-0">{message || t("statistics.load_error")}</div>
            ) : (
              <>
                <div className="row row-cols-xl-5 row-cols-lg-4 row-cols-md-3 row-cols-sm-2 g-3 mb-4">
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.meetings")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedMeetings)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalMeetings)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.connections")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedConnections)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalConnections)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.transcripts")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedTranscripts)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalTranscripts)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.agenda")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedAgendas)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalAgendas)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.decisions")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedDecisions)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalDecisions)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.assembly_ai")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedAssemblyAI)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalAssemblyAI)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.speakers")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedSpeakers)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalSpeakers)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.speech_analysis")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedSpeechAnalysis)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalSpeechAnalysis)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.completed")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedCompleted)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalCompleted)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.agenda_match")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedAgendaMatch)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalAgendaMatch)}</small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-lg-8">
                    <div className="card h-100 mb-0">
                      <div className="card-body">
                        <h5 className="card-title">{t("statistics.trend_title")}</h5>
                        <Line data={lineData} />
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="card h-100 mb-0">
                      <div className="card-body">
                        <h5 className="card-title">{t("statistics.distribution_title")}</h5>
                        <Doughnut data={doughnutData} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <div className="card mb-0">
                      <div className="card-body">
                        <h5 className="card-title">{t("statistics.period_comparison_title")}</h5>
                        <Bar data={barData} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-12">
                    <div className="card mb-0">
                      <div className="card-body">
                        <h5 className="card-title">{t("statistics.latest_transcripts")}</h5>
                        <div className="table-responsive">
                          <table className="table table-striped mb-0">
                            <thead>
                              <tr>
                                <th>{t("statistics.columns.id")}</th>
                                <th>{t("statistics.columns.meeting_id")}</th>
                                <th>{t("statistics.columns.user_id")}</th>
                                <th>{t("statistics.columns.source_language")}</th>
                                <th>{t("statistics.columns.target_language")}</th>
                                <th>{t("statistics.columns.duration")}</th>
                                <th>{t("statistics.columns.created_at")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {latestTranscripts.length === 0 ? (
                                <tr>
                                  <td colSpan="9" className="text-muted">
                                    {isLoading ? t("statistics.loading") : t("statistics.no_data")}
                                  </td>
                                </tr>
                              ) : (
                                latestTranscripts.map((item) => (
                                  <tr key={item.id}>
                                    <td>{item.id}</td>
                                    <td>{item.meetingId || "-"}</td>
                                    <td>{item.userId || "-"}</td>
                                    <td>{item.sourceLanguage || "-"}</td>
                                    <td>{item.targetLanguage || "-"}</td>
                                    <td>{item.duration || "-"}</td>
                                    <td>{formatDate(asMillis(item.createdAt))}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                        <div className="mt-3">
                          <h6 className="mb-2">{t("statistics.latest_assembly")}</h6>
                          <div className="table-responsive">
                            <table className="table table-striped mb-0">
                              <thead>
                                <tr>
                                  <th>{t("statistics.columns.id")}</th>
                                  <th>{t("statistics.columns.meeting_id")}</th>
                                  <th>{t("statistics.columns.user_id")}</th>
                                  <th>{t("statistics.columns.agenda")}</th>
                                  <th>{t("statistics.columns.decisions")}</th>
                                  <th>{t("statistics.columns.speakers")}</th>
                                  <th>{t("statistics.columns.speech_analysis")}</th>
                                  <th>{t("statistics.columns.completed")}</th>
                                  <th>{t("statistics.columns.created_at")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredAssembly.length === 0 ? (
                                  <tr>
                                    <td colSpan="9" className="text-muted">
                                      {isLoading ? t("statistics.loading") : t("statistics.no_data")}
                                    </td>
                                  </tr>
                                ) : (
                                  filteredAssembly
                                    .slice()
                                    .sort((a, b) => (asMillis(b?.createdAt) || 0) - (asMillis(a?.createdAt) || 0))
                                    .slice(0, 6)
                                    .map((item) => (
                                      <tr key={item.id}>
                                        <td>{item.id}</td>
                                        <td>{item.meetingId || "-"}</td>
                                        <td>{item.userId || "-"}</td>
                                        <td>{item.hasAgenda ? t("statistics.yes") : t("statistics.no")}</td>
                                        <td>{item.hasDecisions ? t("statistics.yes") : t("statistics.no")}</td>
                                        <td>{item.hasSpeakers ? t("statistics.yes") : t("statistics.no")}</td>
                                        <td>{item.hasSpeechAnalysis ? t("statistics.yes") : t("statistics.no")}</td>
                                        <td>{item.completed ? t("statistics.yes") : t("statistics.no")}</td>
                                        <td>{formatDate(asMillis(item.createdAt))}</td>
                                      </tr>
                                    ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsDashboardPage;
